import * as THREE from 'three';
import { backgroundImageToThreeTransform } from './floorPlanBackgroundImageCoords.js';
import { getBackgroundRenderedSize } from './floorPlanBackgroundImageState.js';

/**
 * @param {{
 *   background: {
 *     source: string;
 *     worldX: number;
 *     worldY: number;
 *     baseWidthMeters: number;
 *     baseHeightMeters: number;
 *     scale?: number;
 *     widthMeters?: number;
 *     heightMeters?: number;
 *     rotationDegrees: number;
 *     opacity: number;
 *     visible: boolean;
 *   };
 *   center: { x: number; y: number };
 *   metersPerPixel: number;
 * }} params
 * @returns {Promise<THREE.Mesh | null>}
 */
export async function buildBackgroundReferenceImagePlane({
  background,
  center,
  metersPerPixel,
}) {
  if (!background?.visible || !background.source) return null;

  const texture = await new Promise((resolve, reject) => {
    new THREE.TextureLoader().load(background.source, resolve, undefined, reject);
  });
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;

  const { widthMeters, heightMeters } =
    Number.isFinite(background.baseWidthMeters) && Number.isFinite(background.baseHeightMeters)
      ? getBackgroundRenderedSize(background)
      : {
          widthMeters: Number(background.widthMeters) || 0,
          heightMeters: Number(background.heightMeters) || 0,
        };

  const geometry = new THREE.PlaneGeometry(widthMeters, heightMeters);
  geometry.rotateX(-Math.PI / 2);

  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: background.opacity < 1,
    opacity: background.opacity,
    depthWrite: false,
    toneMapped: false,
  });

  const mesh = new THREE.Mesh(geometry, material);
  const transform = backgroundImageToThreeTransform({
    worldX: background.worldX,
    worldY: background.worldY,
    baseWidthMeters: background.baseWidthMeters ?? widthMeters,
    baseHeightMeters: background.baseHeightMeters ?? heightMeters,
    scale: background.scale,
    rotationDegrees: background.rotationDegrees,
    metersPerPixel,
    center,
  });
  mesh.position.set(transform.position.x, transform.position.y, transform.position.z);
  mesh.rotation.y = transform.rotationYRadians;
  mesh.renderOrder = -5;
  mesh.userData.isBackgroundReferenceImage = true;
  return mesh;
}

/**
 * @param {THREE.Mesh | null | undefined} mesh
 */
export function disposeBackgroundReferenceImagePlane(mesh) {
  if (!mesh) return;
  mesh.geometry?.dispose?.();
  if (mesh.material instanceof THREE.Material) {
    mesh.material.map?.dispose?.();
    mesh.material.dispose?.();
  }
}
